import { inject, Injectable } from '@angular/core';
import { Observable, of, throwError } from 'rxjs';
import { map, switchMap, tap } from 'rxjs/operators';
import { CampaignService } from 'app/shared/services/backend/campaign.service';
import { ProgramService } from 'app/shared/services/backend/program.service';
import { BudgetObjectAllocation, ProgramAllocation } from 'app/shared/types/budget-object-allocation.interface';
import { GoalsService } from 'app/shared/services/backend/goals.service';
import { ManageTableFullRowValues, ManageTableRow } from '../components/manage-table/manage-table.types';
import { BulkDeleteResponse, BulkOperationResponse } from 'app/shared/types/bulk-operation-response.interface';
import { BudgetTimeframe } from 'app/shared/types/timeframe.interface';
import { BudgetSegmentAmountDO, BudgetSegmentDO } from 'app/shared/types/segment.interface';
import { Campaign, CampaignAllocationDO, CampaignDO } from 'app/shared/types/campaign.interface';
import { Program, ProgramDO } from 'app/shared/types/program.interface';
import { BulkActionTargets } from '@shared/types/bulk-action-targets.type';
import { BudgetDataService } from '../../dashboard/budget-data/budget-data.service';
import { BulkActionPayloads } from '../types/bulk-action-payloads.type';
import { SegmentedBudgetObject } from 'app/shared/types/segmented-budget-object';
import { BudgetSegmentService } from 'app/shared/services/backend/budget-segment.service';
import { BudgetAllocationService } from 'app/shared/services/backend/budget-allocation.service';
import { getNumericValue } from 'app/shared/utils/common.utils';
import { Goal } from 'app/shared/types/goal.interface';
import { IntegratedObjectsService } from '../../metric-integrations/services/integrated-objects.service';
import { ExternalIntegrationObjectTypes } from '@shared/constants/external-integration-object-types';
import { CompanyDataService } from '@shared/services/company-data.service';
import { adsExpenseSources } from '@shared/types/expense.interface';
import { ExpensesService } from '@shared/services/backend/expenses.service';
import { DeepPartial } from '@shared/types/deep-partial.type';
import { ManageTableRowType } from '@shared/enums/manage-table-row-type.enum';

interface DeleteObjectsResult {
  goals?: BulkDeleteResponse;
  campaigns?: BulkDeleteResponse;
  expGroups?: BulkDeleteResponse;
}

export interface UpdateObjectsResult {
  campaigns?: BulkOperationResponse<CampaignDO>;
  expGroups?: BulkOperationResponse<ProgramDO>;
}

export interface UpdatedSegmentAmountsData {
  amounts: BudgetSegmentAmountDO[];
  timeframe: BudgetTimeframe;
}

@Injectable({
  providedIn: 'root'
})
export class ManagePageApiService {
  private readonly goalService = inject(GoalsService);
  private readonly campaignService = inject(CampaignService);
  private readonly programService = inject(ProgramService);
  private readonly expenseService = inject(ExpensesService);
  private readonly budgetDataService = inject(BudgetDataService);
  private readonly companyDataService = inject(CompanyDataService);
  private readonly budgetSegmentService = inject(BudgetSegmentService);
  private readonly budgetAllocationService = inject(BudgetAllocationService);
  private readonly integratedObjectsService = inject(IntegratedObjectsService);

  private readonly ERROR_MESSAGE = {
    UNSUPPORTED_RECORD_TYPE: `Can't update this record type`,
    FAILED_TO_UPDATE_SEGMENT_ALLOCATION: 'Failed to update segment allocation',
  };

  private runBulkOperationRequest(
    data: any[],
    resultItem: BulkOperationResponse<any>,
    apiMethod: Function
  ): Observable<BulkDeleteResponse> {
    if (!data.length) {
      return of(null);
    }

    return apiMethod(data).pipe(
      tap((response: BulkOperationResponse<any>) => {
        resultItem.success = response.success;
        resultItem.error = response.error;
      })
    );
  }

  public sumUpBulkOperationResults(results: BulkOperationResponse<any>[]): { success: number; error: number } {
    return results.reduce((count, item) => ({
      success: count.success + getNumericValue(item?.success?.length),
      error: count.error + getNumericValue(item?.error?.length),
    }), { success: 0, error: 0 });
  }

  public updateObjectAllocation(
    record: ManageTableRow,
    allocId: number,
    payload: Partial<BudgetObjectAllocation>
  ): Observable<ProgramAllocation | CampaignAllocationDO> {
    switch (record.type) {
      case ManageTableRowType.Campaign:
        return this.campaignService.updateCampaignAllocation(allocId, payload);

      case ManageTableRowType.ExpenseGroup:
        return this.programService.updateProgramAllocation(allocId, payload);

      default:
        throw new Error(this.ERROR_MESSAGE.UNSUPPORTED_RECORD_TYPE);
    }
  }

  public updateObject(
    record: ManageTableRow,
    payload: Object,
    suppressTimeframeAllocations: boolean
  ): Observable<SegmentedBudgetObject> {
    switch (record.type) {
      case ManageTableRowType.Campaign:
        return this.campaignService.updateCampaign(record.objectId, payload)
          .pipe(
            switchMap(updatedCampaign => (
              (record.sharedCostRuleId && !suppressTimeframeAllocations
                ? this.campaignService.split(record.objectId)
                : of(null)
              ).pipe(map(() => this.budgetDataService.convertCampaignDO(updatedCampaign)))
            )),
          );

      case ManageTableRowType.ExpenseGroup:
        return this.programService.updateProgram(record.objectId, payload)
          .pipe(
            switchMap(updatedExpGroup => (
              (record.sharedCostRuleId && !suppressTimeframeAllocations
                  ? this.programService.split(record.objectId)
                  : of(null)
              ).pipe(map(() => this.budgetDataService.convertProgram(updatedExpGroup)))
            )),
          );

      default:
        throw new Error(this.ERROR_MESSAGE.UNSUPPORTED_RECORD_TYPE);
    }
  }

  public deleteObjects(targets: BulkActionTargets): Observable<DeleteObjectsResult> {
    const { goals = [], campaigns = [], expGroups = [] } = targets;
    const result: DeleteObjectsResult = {
      goals: {
        success: [],
        error: []
      },
      campaigns: {
        success: [],
        error: []
      },
      expGroups: {
        success: [],
        error: []
      }
    };

    return this.getIntegratedExpenseIds$(campaigns).pipe(
      switchMap(expenseIds =>
        expenseIds?.length ?
          this.expenseService.deleteMultiExpenses(expenseIds) :
          of(null)
      ),
      switchMap(() => this.runBulkOperationRequest(
        goals,
        result.goals,
        (ids: number[]) => this.goalService.deleteMultiGoals(ids)
      )),
      switchMap(() => this.runBulkOperationRequest(
        campaigns,
        result.campaigns,
        (ids: number[]) => this.campaignService.deleteMultiCampaigns(ids)
      )),
      switchMap(() => this.runBulkOperationRequest(
        expGroups,
        result.expGroups,
        (ids: number[]) => this.programService.deleteMultiPrograms(ids)
      )),
      map(() => result)
    );
  }

  private getIntegratedExpenseIds$(campaignIds: number[]): Observable<number[]> {
    const integrationCampaignTypeNames = Object.values(ExternalIntegrationObjectTypes);

    const adsTypeIds =
      this.companyDataService.campaignTypesSnapshot.filter(
        type => integrationCampaignTypeNames.includes(type.name)
      ).map(
        type => type.id
      );

    const integratedCampaignIds =
      campaignIds?.filter(
        campaignId => {
          const campaign =
            (this.budgetDataService.campaignsSnapshot || this.budgetDataService.lightCampaignsSnapshot)?.find(
              camp => camp.id === campaignId
            );
          return adsTypeIds.includes(campaign.campaignTypeId);
        }
      );

    return this.integratedObjectsService.getExpenseIds$(
      integratedCampaignIds,
      null,
      {
        company: this.companyDataService.selectedCompanySnapshot.id,
        budget: this.budgetDataService.selectedBudgetSnapshot.id,
        source: adsExpenseSources.join(',')
      }
    );
  }

  public updateObjects(payloads: BulkActionPayloads<any>): Observable<UpdateObjectsResult> {
    const { campaigns = [], expGroups = [] } = payloads;
    const result: UpdateObjectsResult = {
      campaigns: {
        success: [],
        error: []
      },
      expGroups: {
        success: [],
        error: []
      }
    };

    return this.runBulkOperationRequest(campaigns, result.campaigns, (data) => this.campaignService.updateMultiCampaigns(data))
      .pipe(
        switchMap(() => this.runBulkOperationRequest(
          expGroups,
          result.expGroups,
          data => this.programService.updateMultiPrograms(data)
        )),
        map(() => result)
      );
  }

  public duplicateAndFetchObject(target: ManageTableRow): Observable<Goal | Campaign | Program > {
    switch (target.type) {
      case ManageTableRowType.Goal:
        return this.goalService.cloneGoal(target.objectId)
          .pipe(
            switchMap(result => this.goalService.getGoal(result.id)),
            map(goal => this.budgetDataService.convertGoal(goal))
          );

      case ManageTableRowType.Campaign:
        return this.campaignService.cloneCampaign(target.objectId)
          .pipe(
            switchMap(result => this.campaignService.getCampaign(result.id)),
            map(campaign => this.budgetDataService.convertCampaignDO(campaign))
          );

      case ManageTableRowType.ExpenseGroup:
        return this.programService.cloneProgram(target.objectId)
          .pipe(
            switchMap(result => this.programService.getProgram(result.id)),
            map(expGroup => this.budgetDataService.convertProgram(expGroup))
          );

      default:
        throw new Error(this.ERROR_MESSAGE.UNSUPPORTED_RECORD_TYPE);
    }
  }

  public createObjectFromTemplate(targetType: ManageTableRowType, template: DeepPartial<CampaignDO | ProgramDO>): Observable<Campaign | Program> {
    switch (targetType) {
      case ManageTableRowType.Campaign:
        return this.campaignService.createCampaign(template)
          .pipe(
            map(campaignDO => this.budgetDataService.convertCampaignDO(campaignDO))
          );

      case ManageTableRowType.ExpenseGroup:
        return this.programService.createProgram(template)
          .pipe(
            map(expGroupDO => this.budgetDataService.convertProgram(expGroupDO))
          );

      default:
        throw new Error(this.ERROR_MESSAGE.UNSUPPORTED_RECORD_TYPE);
    }
  }

  public updateSegmentAllocation(params: {
    objectId: number,
    budgetSegments: BudgetSegmentDO[],
    timeframeId: number,
    segmentAmount: number,
    timeframes: BudgetTimeframe[],
    grandTotal: ManageTableFullRowValues
  }): Observable<UpdatedSegmentAmountsData> {
    const { objectId, budgetSegments, timeframeId, timeframes, grandTotal, segmentAmount } = params;
    const targetBudgetSegment = budgetSegments.find(item => item.id === objectId);
    const targetSegmentAmount = (targetBudgetSegment?.amounts || []).find(item => item.company_budget_alloc === timeframeId);
    const targetTimeframe = timeframes.find(item => item.id === timeframeId);
    const timeframeValue = grandTotal.segment.values[timeframeId]?.allocated;

    if (!targetTimeframe || !targetSegmentAmount || timeframeValue == null) {
      return throwError(new Error(this.ERROR_MESSAGE.FAILED_TO_UPDATE_SEGMENT_ALLOCATION));
    }

    const updateAmountsPayload: Partial<BudgetSegmentAmountDO>[] = [{
      id: targetSegmentAmount.id,
      amount: segmentAmount
    }];
    const updateTimeframePayload: Partial<BudgetTimeframe> = { amount: timeframeValue };

    return this.budgetSegmentService.updateBudgetSegmentAmounts(updateAmountsPayload)
      .pipe(
        switchMap(updatedAmounts => this.budgetAllocationService.updateBudgetAllocation(targetTimeframe.id, updateTimeframePayload)
          .pipe(
            map(updatedTimeframe => ({
              amounts: updatedAmounts,
              timeframe: updatedTimeframe
            }))
          )
        )
      );
  }
}
